import { useAuth, type Operator } from './auth';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code: string,
  ) {
    super(message);
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = useAuth.getState().token;
  const res = await fetch(path, {
    method,
    headers: {
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const json = (await res.json().catch(() => null)) as
    | { success: true; data: T }
    | { success: false; error: { code: string; message: string } }
    | null;
  if (!res.ok || !json || json.success === false) {
    const err = json && json.success === false ? json.error : null;
    if (res.status === 401 && path !== '/api/auth/login') useAuth.getState().logout();
    throw new ApiError(res.status, err?.message ?? `Eroare ${res.status}`, err?.code ?? 'ERROR');
  }
  return json.data;
}

// --- Types mirrored from the console API -----------------------------------

export type TenantStatus = 'ACTIVE' | 'SUSPENDED';
export type InvoiceStatus = 'DRAFT' | 'SENT' | 'PAID' | 'CANCELLED';

export interface AiProviderUsage {
  costEur: number;
  callCount: number;
}

export interface HeartbeatView {
  at: string;
  version: string;
  uptimeSec: number;
  usersActive: number;
  clientsActive: number;
  suppliersActive: number;
  subscriptionsActive: number;
  tasksTotal: number;
  tasksOpen: number;
  projects: number;
  companies: number;
  dbSizeMb: number;
  aiMonth: string;
  aiBudgetEur: number | null;
  aiUsage: Record<string, AiProviderUsage>;
  aiTotalEur: number;
}

export type AiTier = 'haiku' | 'sonnet' | 'opus';
export const AI_TIERS: readonly AiTier[] = ['haiku', 'sonnet', 'opus'];

export interface AiModelsPolicy {
  allowed: AiTier[];
  default: 'auto' | AiTier;
}

/** One row of a tenant's AI spend per model, for one month. */
export interface AiUsageRow {
  provider: string;
  model: string;
  inputTokens: number;
  outputTokens: number;
  callCount: number;
  costEur: number;
}

export interface TenantAiUsage {
  month: string;
  /** Months on record, newest first. */
  months: string[];
  rows: AiUsageRow[];
  totals: { costEur: number; callCount: number };
}

/** Where the instance's key for one provider comes from (never the key itself). */
export interface MaskedKey {
  /** Whether a key is stored for this tenant. */
  set: boolean;
  last4: string | null;
}

/** Who pays for the tenant's AI: 'firmio' = CCC keys in the subscription, 'own' = the customer's. */
export interface AiBillingView {
  source: 'firmio' | 'own' | null;
  budgetEur: number | null;
  anthropic: MaskedKey;
  openai: MaskedKey;
}

export interface AiBillingInput {
  source: 'firmio' | 'own' | null;
  budgetEur?: number | null;
  /** Left out = keep the stored key; a string replaces it. */
  anthropicKey?: string;
  openaiKey?: string;
}

export interface AiBudgetUsage {
  pct: number;
  level: 'ok' | 'warn' | 'over';
}

export interface TenantBase {
  id: string;
  slug: string;
  name: string;
  domain: string;
  status: TenantStatus;
  suspendReason: string | null;
  suspendedAt: string | null;
  plan: string | null;
  notes: string | null;
  hostLabel: string | null;
  /** Modules the tenant bought (catalogue order); null = all. */
  allowedModules: string[] | null;
  /** Opt-in modules granted (e.g. 'actors'); off on the instance otherwise. */
  optInModules: string[];
  /** AI models the tenant bought + the chat's default; null = all, env default. */
  aiModels: AiModelsPolicy | null;
  aiBilling: AiBillingView;
  createdAt: string;
  /** Last change of any setting; with lastSeenAt, says whether the instance applied it. */
  updatedAt: string;
}

export interface TenantListItem extends TenantBase {
  online: boolean;
  lastSeenAt: string | null;
  latest: HeartbeatView | null;
  /** Spend vs the subscription's AI limit; null without a Firmio limit. */
  aiBudgetUsage: AiBudgetUsage | null;
  overdueInvoices: number;
}

export interface TenantDetail extends TenantBase {
  online: boolean;
  lastSeenAt: string | null;
  latest: HeartbeatView | null;
  aiBudgetUsage: AiBudgetUsage | null;
  daily: { day: string; usersActive: number; tasksTotal: number; aiTotalEur: number }[];
  offlineAfterMinutes: number;
}

export interface Invoice {
  id: string;
  tenantId: string;
  number: string;
  issuedAt: string;
  dueAt: string;
  amount: number;
  currency: string;
  status: InvoiceStatus;
  overdue: boolean;
  notes: string | null;
  externalUrl: string | null;
}

export interface InvoiceInput {
  number: string;
  issuedAt: string;
  dueAt: string;
  amount: number;
  currency: string;
  status: InvoiceStatus;
  notes?: string | null;
  externalUrl?: string | null;
}

export interface AuditEntry {
  id: string;
  action: string;
  at: string;
  details: Record<string, unknown> | null;
  operator: { name: string; email: string } | null;
  tenant?: { id: string; name: string; slug: string } | null;
}

export interface TenantInput {
  slug: string;
  name: string;
  domain: string;
  plan?: string | null;
  notes?: string | null;
  hostLabel?: string | null;
}

export interface IssuedKey {
  instanceId: string;
  instanceKey: string;
}

// --- Calls -----------------------------------------------------------------

export const api = {
  login: (email: string, password: string) =>
    request<{ accessToken: string; operator: Operator }>('POST', '/api/auth/login', {
      email,
      password,
    }),
  tenants: () => request<TenantListItem[]>('GET', '/api/tenants'),
  tenant: (id: string) => request<TenantDetail>('GET', `/api/tenants/${id}`),
  createTenant: (input: TenantInput) =>
    request<IssuedKey & { tenant: TenantBase }>('POST', '/api/tenants', input),
  updateTenant: (id: string, input: Partial<Omit<TenantInput, 'slug'>>) =>
    request<TenantBase>('PATCH', `/api/tenants/${id}`, input),
  setTenantModules: (id: string, allowed: string[] | null) =>
    request<TenantBase>('PUT', `/api/tenants/${id}/modules`, { allowed }),
  setTenantOptInModules: (id: string, granted: string[]) =>
    request<TenantBase>('PUT', `/api/tenants/${id}/opt-in-modules`, { granted }),
  setTenantAiModels: (id: string, policy: AiModelsPolicy | null) =>
    request<TenantBase>('PUT', `/api/tenants/${id}/ai-models`, { policy }),
  setTenantAiBilling: (id: string, input: AiBillingInput) =>
    request<TenantBase>('PUT', `/api/tenants/${id}/ai-billing`, input),
  tenantAiUsage: (id: string, month?: string) =>
    request<TenantAiUsage>(
      'GET',
      `/api/tenants/${id}/ai-usage${month ? `?month=${encodeURIComponent(month)}` : ''}`,
    ),
  suspend: (id: string, reason: string) =>
    request<TenantBase>('POST', `/api/tenants/${id}/suspend`, { reason }),
  activate: (id: string) => request<TenantBase>('POST', `/api/tenants/${id}/activate`, {}),
  rotateKey: (id: string) => request<IssuedKey>('POST', `/api/tenants/${id}/rotate-key`, {}),
  invoices: (id: string) => request<Invoice[]>('GET', `/api/tenants/${id}/invoices`),
  createInvoice: (id: string, input: InvoiceInput) =>
    request<Invoice>('POST', `/api/tenants/${id}/invoices`, input),
  updateInvoice: (invoiceId: string, input: Partial<InvoiceInput>) =>
    request<Invoice>('PATCH', `/api/invoices/${invoiceId}`, input),
  deleteInvoice: (invoiceId: string) =>
    request<{ deleted: boolean }>('DELETE', `/api/invoices/${invoiceId}`),
  tenantAudit: (id: string) => request<AuditEntry[]>('GET', `/api/tenants/${id}/audit`),
  audit: () => request<AuditEntry[]>('GET', '/api/audit'),
};
